

import React, { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Select } from "components/ui/Select/Select";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { EDD_METHOD_OPTIONS } from "features/pregnancy/constants/eddMethodOptions";
import { useCreateFoetuses } from "features/pregnancy/hooks/useCreateFoetuses";
import { useMaternityHospitals } from "features/pregnancy/hooks/useMaternityHospitals";
import { useUpdatePregnancy } from "features/pregnancy/hooks/useUpdatePregnancy";
import { calcEdd } from "features/pregnancy/utils/calcEdd";
import { toYMD } from "features/pregnancy/utils/dateFormatters";
import { normaliseEddMethod } from "features/pregnancy/utils/normaliseEddMethod";
import { toPatientCheckAnswer } from "features/pregnancy/utils/patientCheckAnswers";
import { makeEditPregnancyFormSchema } from "features/pregnancy/utils/pregnancyFormSchema";
import { transformStaffOptions } from "features/pregnancy/utils/transformStaffOptions";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "../AddPregnancyForm/AddPregnancyForm.module.css";
import { PatientChecksFields } from "../AddPregnancyForm/PatientChecksFields";

export const EditPregnancyForm = ({
  currentPatientId,
  closeModal,
  tenantVal,
  pregnancy,
}) => {
  const { data: staffMembers } = useStaffMembers();
  const { data: staffInfo } = useStaffInfo();
  /*
   * A pregnancy created before its first hospital message holds no count (KIN-2060). Showing a
   * made-up 1 would turn an unknown into an assertion, and requiring it would stop the GP fixing
   * anything else on the record — so the field opens blank and is only mandatory once the record
   * holds a number worth protecting.
   */
  const hasStoredFoetusCount =
    pregnancy.numberOfFoetuses !== null && pregnancy.numberOfFoetuses !== undefined;

  const form = useForm({
    resolver: zodResolver(
      makeEditPregnancyFormSchema({ hasStoredFoetusCount })
    ),
    defaultValues: {
      gravida: pregnancy.gravida.toString(),
      para: pregnancy.para.toString(),
      /* Read through the mapping, never as a truthiness test: a stored false is an answer the GP
         gave with the old tick boxes and has to open as No, not as Not asked (KIN-2056). */
      hasClinicHCGPositive: toPatientCheckAnswer(pregnancy.confirmHcgPositive),
      smoker: toPatientCheckAnswer(pregnancy.smoker),
      noFamilyHistoryOfCongenitalAbnormality: toPatientCheckAnswer(
        pregnancy.familyHistoryCongentialAbnormality
      ),
      hasHadChickenPox: toPatientCheckAnswer(pregnancy.hasChickenPox),
      lastMenstrualPeriod: toYMD(pregnancy.lastMenstrualPeriod),
      eddMethod: normaliseEddMethod(pregnancy.eddMethod),
      estimatedDueDate: toYMD(pregnancy.estimatedDueDate) ?? "",
      numberOfFoetuses: pregnancy.numberOfFoetuses?.toString() ?? "",
      conductedByStaffMemberId: pregnancy.conductedByStaffMemberId
        ? String(pregnancy.conductedByStaffMemberId)
        : "",
      receivingFacilityCode: pregnancy.receivingFacilityCode
        ? String(pregnancy.receivingFacilityCode)
        : "",
    },
  });

  const { data: hospitalOptions, isLoading: isLoadingHospitals } =
    useMaternityHospitals();

  useEffect(() => {
    if (!staffInfo?.staffMember?.id) return;
    if (form.getValues("conductedByStaffMemberId")) return;
    form.setValue("conductedByStaffMemberId", String(staffInfo.staffMember.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffInfo]);

  const { mutate: createFoetuses } = useCreateFoetuses();

  const { mutate: editPregnancy, isPending } = useUpdatePregnancy({
    patientId: currentPatientId,
    tenantVal,
    pregnancyId: pregnancy.id,
    onSuccess: () => {
      const fetusCount = parseInt(
        form.getValues("numberOfFoetuses") || "1",
        10
      );

      if (fetusCount !== (pregnancy.numberOfFoetuses ?? 0)) {
        createFoetuses({ pregnancyId: pregnancy.id, count: fetusCount });
      }

      form.reset();
      closeModal();
    },
  });

  return (
    <FormProvider {...form}>
      <form
        className={style.addPregnancyForm}
        onSubmit={form.handleSubmit((values) => editPregnancy(values), onInvalidSubmit("EditPregnancyForm"))}
      >
        {(() => {
          const isLmp =
            !form.watch("eddMethod") || form.watch("eddMethod") === "LMP";

          return (
            <>
              <div className={style.inputGroupGrid4}>
                <FormField name="lastMenstrualPeriod">
                  <label className={style.pregnancyFormLabel}>
                    Last Menstrual Period
                  </label>
                  <DatePickerField
                    value={form.watch("lastMenstrualPeriod")}
                    onChange={(val) =>
                      form.setValue("lastMenstrualPeriod", val, {
                        shouldValidate: true,
                      })
                    }
                  />
                </FormField>
                <FormField name="conductedByStaffMemberId">
                  <label className={style.pregnancyFormLabel}>Doctor</label>
                  <Select
                    options={transformStaffOptions(staffMembers)}
                    value={form.watch("conductedByStaffMemberId") ?? ""}
                    onChange={(value) =>
                      form.setValue("conductedByStaffMemberId", value, {
                        shouldValidate: true,
                      })
                    }
                    placeholder="Select doctor"
                    fullWidth
                  />
                </FormField>
                <FormField name="eddMethod">
                  <label className={style.pregnancyFormLabel}>
                    Estimated Due Date Method
                  </label>
                  <Controller
                    control={form.control}
                    name="eddMethod"
                    render={({ field }) => (
                      <Select
                        name="eddMethod"
                        options={EDD_METHOD_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select EDD method"
                        fullWidth
                      />
                    )}
                  />
                </FormField>
                <FormField name="estimatedDueDate">
                  <label className={style.pregnancyFormLabel}>
                    Estimated Due Date
                  </label>
                  <DatePickerField
                    value={
                      isLmp
                        ? calcEdd(form.watch("lastMenstrualPeriod"))
                        : form.watch("estimatedDueDate")
                    }
                    onChange={(val) =>
                      form.setValue("estimatedDueDate", val, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isLmp}
                  />
                </FormField>
              </div>
              <div className={style.inputGroupGrid4}>
                <FormField name="receivingFacilityCode">
                  <label className={style.pregnancyFormLabel}>Hospital</label>
                  <Select
                    options={hospitalOptions ?? []}
                    value={form.watch("receivingFacilityCode") ?? ""}
                    onChange={(value) =>
                      form.setValue("receivingFacilityCode", value, {
                        shouldValidate: true,
                      })
                    }
                    placeholder={
                      isLoadingHospitals
                        ? "Loading hospitals…"
                        : hospitalOptions?.length
                          ? "Select hospital"
                          : "No hospitals available"
                    }
                    fullWidth
                    disabled={isLoadingHospitals || !hospitalOptions?.length}
                  />
                  {/* Said only while it is empty, and only as what will happen: the backend sets
                      the hospital from the first message's MSH.4 while the field is blank, so a GP
                      looking at an empty select is not looking at something they forgot (KIN-2060).
                      Once a value is there it stays theirs to change — a transfer of care, or a
                      wrong hospital, can only be corrected here. */}
                  {!form.watch("receivingFacilityCode") && (
                    <p className={style.eddHint}>
                      Filled in from the maternity hospital&apos;s first message.
                    </p>
                  )}
                </FormField>
                <FormField name="numberOfFoetuses">
                  <label className={style.pregnancyFormLabel}>
                    Number of foetuses
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                    }
                    {...form.register("numberOfFoetuses")}
                  />
                </FormField>
                <FormField name="gravida">
                  <label className={style.pregnancyFormLabel}>Gravida</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                    }
                    {...form.register("gravida")}
                  />
                </FormField>
                <FormField name="para">
                  <label className={style.pregnancyFormLabel}>Para</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                    }
                    {...form.register("para")}
                  />
                </FormField>
              </div>
            </>
          );
        })()}

        <PatientChecksFields control={form.control} />

        <DialogFooter className={style.formFooter}>
          <Button variant="outline" onClick={closeModal} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            Update
          </Button>
        </DialogFooter>
      </form>
    </FormProvider>
  );
};
